import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock, Globe, Loader2, User, Trash2, Share2, Copy, Check, KeyRound, Link2 } from 'lucide-react';
import { Button, Card, Modal } from '../components/UIComponents';
import { SearchableSelect } from '../components/SearchableSelect';
import UsageStats from '../components/UsageStats';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { useAuth } from '../hooks/useAuth';
import { useQuota, useUsage, useSubscription } from '../hooks/useBilling';
import { handleApiError } from '../api/client';
import {
  deleteAccount,
  updateProfile,
  getReferralStats,
  type UpdateProfileRequest,
  type ReferralStats,
} from '../api/user';
import { createApiKey, deleteApiKey, listApiKeys, revokeApiKey } from '../api/apiKeys';
import { getOAuthAccounts } from '../api/oauth';
import {
  LANGUAGE_OPTIONS,
  getTimeZoneOptionsWithOffset,
} from '../data/settingsOptions';
import { useTranslation } from 'react-i18next';
import type { ApiKey, OAuthAccount } from '../types';
import { useLanguage } from '../components/LanguageProvider';

function formatDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatMinutes(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
  }
  return `${Math.round(minutes)}m`;
}

type UsageSummaryLike = {
  total_minutes?: number;
  transcription_count?: number;
  translation_count?: number;
  summary_count?: number;
  mindmap_count?: number;
  keypoints_count?: number;
  export_count?: number;
  by_type?: Record<string, { minutes?: number; count?: number }>;
  TotalMinutes?: number;
  TranscriptionCount?: number;
  TranslationCount?: number;
  SummaryCount?: number;
  MindmapCount?: number;
  KeypointsCount?: number;
  ExportCount?: number;
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const { user, refreshUser, logout } = useAuth();
  const { data: quota, loading: quotaLoading, error: quotaError } = useQuota();
  const { data: subscription, loading: subscriptionLoading } = useSubscription({ enabled: !!user });

  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(() =>
    formatDateInput(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000))
  );
  const [endDate, setEndDate] = useState(() => formatDateInput(today));
  const { data: usage, loading: usageLoading, error: usageError } = useUsage(startDate, endDate);

  const [profileForm, setProfileForm] = useState({ name: '' });
  const [preferencesForm, setPreferencesForm] = useState({ language: 'en', timezone: 'UTC' });

  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsError, setPrefsError] = useState('');
  const [prefsSuccess, setPrefsSuccess] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  const [oauthAccounts, setOAuthAccounts] = useState<OAuthAccount[] | null>(null);
  const [oauthAccountsLoading, setOAuthAccountsLoading] = useState(false);
  const [oauthAccountsError, setOAuthAccountsError] = useState('');
  const [failedAvatarUrls, setFailedAvatarUrls] = useState<Record<string, boolean>>({});

  const isPaidUser = useMemo(() => {
    if (subscriptionLoading) return false;
    if (!subscription) return false;
    if (subscription.status !== 'active' && subscription.status !== 'trialing') return false;
    return subscription.plan_id !== 'free';
  }, [subscription, subscriptionLoading]);

  const [apiKeys, setApiKeys] = useState<ApiKey[] | null>(null);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState('');
  const [createApiKeyName, setCreateApiKeyName] = useState('');
  const [createApiKeyLoading, setCreateApiKeyLoading] = useState(false);
  const [newApiKeyPlain, setNewApiKeyPlain] = useState('');
  const [newApiKeyModalOpen, setNewApiKeyModalOpen] = useState(false);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [deleteApiKeyModalOpen, setDeleteApiKeyModalOpen] = useState(false);
  const [deleteApiKeyTarget, setDeleteApiKeyTarget] = useState<ApiKey | null>(null);
  const [deleteApiKeyLoading, setDeleteApiKeyLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfileForm({
      name: user.name || '',
    });
    setPreferencesForm({
      language: user.language || 'en',
      timezone: user.timezone || 'UTC',
    });

    setReferralLoading(true);
    getReferralStats()
      .then(setReferralStats)
      .catch(err => setReferralError(handleApiError(err)))
      .finally(() => setReferralLoading(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (!isPaidUser) {
      setApiKeys(null);
      return;
    }
    setApiKeysError('');
    setApiKeysLoading(true);
    listApiKeys()
      .then(setApiKeys)
      .catch(err => setApiKeysError(handleApiError(err)))
      .finally(() => setApiKeysLoading(false));
  }, [user, isPaidUser]);

  useEffect(() => {
    if (!user) {
      setOAuthAccounts(null);
      return;
    }

    setOAuthAccountsError('');
    setOAuthAccountsLoading(true);
    getOAuthAccounts()
      .then(setOAuthAccounts)
      .catch(err => setOAuthAccountsError(handleApiError(err)))
      .finally(() => setOAuthAccountsLoading(false));
  }, [user]);

  const timeZoneSelectOptions = useMemo(() => getTimeZoneOptionsWithOffset(), []);

  const isAvatarFailed = (avatarUrl?: string) => {
    if (!avatarUrl) {
      return false;
    }
    return !!failedAvatarUrls[avatarUrl];
  };

  const markAvatarFailed = (avatarUrl?: string) => {
    if (!avatarUrl) {
      return;
    }
    setFailedAvatarUrls((prev) => {
      if (prev[avatarUrl]) {
        return prev;
      }
      return { ...prev, [avatarUrl]: true };
    });
  };

  const getUserInitials = () => {
    const displayName = user?.name?.trim() || user?.email?.trim() || '';
    if (!displayName) return 'U';

    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  const usageSummary = usage?.usage as UsageSummaryLike | undefined;
  const totalMinutes = usageSummary?.total_minutes ?? usageSummary?.TotalMinutes ?? 0;
  const typeCounts = {
    transcription:
      usageSummary?.transcription_count ?? usageSummary?.TranscriptionCount ?? 0,
    translation:
      usageSummary?.translation_count ?? usageSummary?.TranslationCount ?? 0,
    summary: usageSummary?.summary_count ?? usageSummary?.SummaryCount ?? 0,
    mindmap: usageSummary?.mindmap_count ?? usageSummary?.MindmapCount ?? 0,
    keypoints: usageSummary?.keypoints_count ?? usageSummary?.KeypointsCount ?? 0,
    export: usageSummary?.export_count ?? usageSummary?.ExportCount ?? 0,
  };

  const breakdownEntries = useMemo(() => {
    if (usageSummary?.by_type) {
      return Object.entries(usageSummary.by_type).map(([type, stats]) => ({
        type,
        minutes: stats.minutes ?? 0,
        count: stats.count ?? 0,
      }));
    }
    return Object.entries(typeCounts).map(([type, count]) => ({
      type,
      minutes: null,
      count,
    }));
  }, [usageSummary, typeCounts]);

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (value > endDate) {
      setEndDate(value);
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    if (value < startDate) {
      setStartDate(value);
    }
  };

  const handleProfileSave = async () => {
    if (!user) return;
    setProfileError('');
    setProfileSuccess('');

    const updates: UpdateProfileRequest = {};
    if (profileForm.name !== user.name) {
      updates.name = profileForm.name;
    }

    if (Object.keys(updates).length === 0) {
      setProfileSuccess(t('settings.profile.noChanges'));
      return;
    }

    setProfileSaving(true);
    try {
      await updateProfile(updates);
      await refreshUser();
      setProfileSuccess(t('settings.profile.updated'));
    } catch (error) {
      setProfileError(handleApiError(error));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePreferencesSave = async () => {
    if (!user) return;
    setPrefsError('');
    setPrefsSuccess('');

    const updates: UpdateProfileRequest = {};
    if (preferencesForm.language !== user.language) {
      updates.language = preferencesForm.language;
    }
    if (preferencesForm.timezone !== user.timezone) {
      updates.timezone = preferencesForm.timezone;
    }

    if (Object.keys(updates).length === 0) {
      setPrefsSuccess(t('settings.profile.noChanges'));
      return;
    }

    setPrefsSaving(true);
    try {
      await updateProfile(updates);
      if (updates.language) {
        setLanguage(updates.language);
      }
      await refreshUser();
      setPrefsSuccess(t('settings.preferences.updated'));
    } catch (error) {
      setPrefsError(handleApiError(error));
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) {
      setDeleteError(t('settings.danger.passwordRequired'));
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteAccount({ password: deletePassword });
      await logout();
      navigate('/');
    } catch (error) {
      setDeleteError(handleApiError(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    setApiKeysError('');
    const trimmedName = createApiKeyName.trim();
    if (!trimmedName) {
      setApiKeysError(t('settings.apiKeys.nameRequired', { defaultValue: 'Name is required.' }));
      return;
    }
    setCreateApiKeyLoading(true);
    try {
      const resp = await createApiKey(trimmedName);
      setNewApiKeyPlain(resp.api_key);
      setNewApiKeyModalOpen(true);
      setCreateApiKeyName('');
      const updated = await listApiKeys();
      setApiKeys(updated);
    } catch (error) {
      setApiKeysError(handleApiError(error));
    } finally {
      setCreateApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(newApiKeyPlain);
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 1500);
    } catch {
      // Ignore clipboard failures (e.g., unsupported environments).
    }
  };

  const openRevokeModal = (key: ApiKey) => {
    setRevokeTarget(key);
    setRevokeModalOpen(true);
  };

  const openDeleteApiKeyModal = (key: ApiKey) => {
    setDeleteApiKeyTarget(key);
    setDeleteApiKeyModalOpen(true);
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeLoading(true);
    setApiKeysError('');
    try {
      await revokeApiKey(revokeTarget.id);
      const updated = await listApiKeys();
      setApiKeys(updated);
      setRevokeModalOpen(false);
      setRevokeTarget(null);
    } catch (error) {
      setApiKeysError(handleApiError(error));
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleConfirmDeleteApiKey = async () => {
    if (!deleteApiKeyTarget) return;
    setDeleteApiKeyLoading(true);
    setApiKeysError('');
    try {
      await deleteApiKey(deleteApiKeyTarget.id);
      const updated = await listApiKeys();
      setApiKeys(updated);
      setDeleteApiKeyModalOpen(false);
      setDeleteApiKeyTarget(null);
    } catch (error) {
      setApiKeysError(handleApiError(error));
    } finally {
      setDeleteApiKeyLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center justify-center min-h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="container mx-auto px-4 max-w-5xl space-y-8">
        <header>
          <h1 className="h-page">{t('settings.title')}</h1>
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            {t('settings.subtitle')}
          </p>
        </header>

        <Card className="p-0 overflow-hidden">
          <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 dark:border-zinc-800">
            <div className="flex items-center gap-2.5">
              <User className="h-4 w-4 text-slate-500 dark:text-zinc-400" />
              <div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white">{t('settings.profile.title')}</h2>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  {t('settings.profile.description', { defaultValue: 'Manage your public display name and account identity.' })}
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            {profileError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{profileError}</p>
              </div>
            )}

            {profileSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-700 dark:text-emerald-300">{profileSuccess}</p>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-100 text-base font-semibold text-slate-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 flex items-center justify-center">
                {user.avatar_url && !isAvatarFailed(user.avatar_url) ? (
                  <img
                    src={user.avatar_url}
                    alt={t('settings.profile.avatarAlt', { defaultValue: 'Profile photo' })}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                    onError={() => markAvatarFailed(user.avatar_url)}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  getUserInitials()
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-semibold text-slate-900 dark:text-zinc-100">
                    {user.name || user.email}
                  </p>
                  {(oauthAccounts || []).some((account) => account.provider === 'google') && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:border-zinc-700 dark:bg-zinc-800/70 dark:text-zinc-300">
                      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-[9px] font-bold text-blue-600">
                        G
                      </span>
                      Google
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-slate-500 dark:text-zinc-400">{user.email}</p>
                <p className="mt-1.5 text-xs leading-5 text-slate-500 dark:text-zinc-500">
                  {t('settings.profile.avatarManagedByProvider', { defaultValue: 'Profile photo is managed by your linked sign-in provider.' })}
                </p>
              </div>
            </div>

            <div className="my-5 border-t border-slate-100 dark:border-zinc-800" />

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">{t('settings.profile.fullName')}</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(event) =>
                    setProfileForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">{t('settings.profile.email')}</label>
                <input
                  type="text"
                  value={user.email}
                  disabled
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/50 px-3 py-2 text-sm text-slate-500 dark:text-zinc-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <Button size="sm" onClick={handleProfileSave} isLoading={profileSaving}>
                {t('settings.profile.save')}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Share2 className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('settings.referral.title')}</h2>
          </div>

          {referralLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t('settings.referral.loading')}</span>
            </div>
          ) : referralError ? (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{referralError}</p>
            </div>
          ) : referralStats ? (
            <div className="space-y-6">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-2">{t('settings.referral.subtitle')}</h3>
                <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
                  {t('settings.referral.description', { minutes: 60 })}
                </p>
                
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white dark:bg-zinc-900 border border-indigo-200 dark:border-indigo-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                    {window.location.origin}/register?ref={referralStats.invitation_code}
                  </div>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => {
                      const link = `${window.location.origin}/register?ref=${referralStats.invitation_code}`;
                      navigator.clipboard.writeText(link).then(() => {
                        setCopiedLink(true);
                        setTimeout(() => setCopiedLink(false), 2000);
                      }).catch(console.error);
                    }}
                  >
                    {copiedLink ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copiedLink ? t('common.copied') : t('settings.referral.copyLink')}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="border border-slate-200 dark:border-zinc-700 rounded-lg p-4">
                  <p className="text-xs text-slate-500 dark:text-zinc-500 uppercase font-semibold mb-1">{t('settings.referral.totalInvited')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{referralStats.total_invited}</p>
                </div>
                <div className="border border-slate-200 dark:border-zinc-700 rounded-lg p-4">
                  <p className="text-xs text-slate-500 dark:text-zinc-500 uppercase font-semibold mb-1">{t('settings.referral.minutesEarned')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{referralStats.total_earned}m</p>
                </div>
              </div>

              {referralStats.referrals.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">{t('settings.referral.history')}</h3>
                  <div className="border border-slate-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-zinc-800/50 text-slate-500 dark:text-zinc-400">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">{t('settings.referral.status')}</th>
                          <th className="px-4 py-2 text-left font-medium">{t('settings.referral.date')}</th>
                          <th className="px-4 py-2 text-right font-medium">{t('settings.referral.reward')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-zinc-700">
                        {referralStats.referrals.map((ref) => (
                          <tr key={ref.id}>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                ref.status === 'completed' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              }`}>
                                {ref.status === 'completed' ? t('settings.referral.completed') : t('settings.referral.pending')}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-600 dark:text-zinc-400">
                              {new Date(ref.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 text-right text-slate-900 dark:text-white font-medium">
                              {ref.reward_amount > 0 ? `+${ref.reward_amount}m` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('settings.usage.title')}</h2>
          </div>

          {quotaLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t('settings.usage.loadingQuota')}</span>
            </div>
          ) : quotaError ? (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{quotaError}</p>
            </div>
          ) : quota ? (
            <UsageStats quota={quota} />
          ) : (
            <p className="text-sm text-slate-500 dark:text-zinc-500">{t('settings.usage.noQuota')}</p>
          )}

          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('settings.usage.breakdown')}</h3>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  {t('settings.usage.dateRangeNote')}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => handleStartDateChange(event.target.value)}
                  className="rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-slate-400 dark:text-zinc-500 text-sm">{t('common.to')}</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => handleEndDateChange(event.target.value)}
                  className="rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {usageLoading ? (
              <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t('settings.usage.loadingUsage')}</span>
              </div>
            ) : usageError ? (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{usageError}</p>
              </div>
            ) : usageSummary ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2 mb-4">
                  <div className="rounded-lg border border-slate-200 dark:border-zinc-700 p-3">
                    <p className="text-xs text-slate-500 dark:text-zinc-400">{t('settings.usage.totalMinutes')}</p>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">
                      {formatMinutes(totalMinutes)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-zinc-700 p-3">
                    <p className="text-xs text-slate-500 dark:text-zinc-400">{t('settings.usage.range')}</p>
                    <p className="text-sm text-slate-700 dark:text-zinc-300">
                      {usage?.period?.start || startDate} - {usage?.period?.end || endDate}
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto border border-slate-200 dark:border-zinc-700 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-zinc-800/50 text-slate-500 dark:text-zinc-400">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">{t('settings.usage.type')}</th>
                        <th className="px-4 py-2 text-left font-medium">{t('settings.usage.minutes')}</th>
                        <th className="px-4 py-2 text-left font-medium">{t('settings.usage.count')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-zinc-700">
                      {breakdownEntries.map((entry) => (
                        <tr key={entry.type}>
                          <td className="px-4 py-2 text-slate-700 dark:text-zinc-300 capitalize">
                            {entry.type.replace('_', ' ')}
                          </td>
                          <td className="px-4 py-2 text-slate-600 dark:text-zinc-400">
                            {entry.minutes === null ? '-' : formatMinutes(entry.minutes)}
                          </td>
                          <td className="px-4 py-2 text-slate-600 dark:text-zinc-400">
                            {entry.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-zinc-500">{t('settings.usage.noUsage')}</p>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Globe className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('settings.preferences.title')}</h2>
          </div>

          {prefsError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{prefsError}</p>
            </div>
          )}

          {prefsSuccess && (
            <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700 dark:text-emerald-300">{prefsSuccess}</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">{t('settings.preferences.language')}</label>
              <SearchableSelect
                options={LANGUAGE_OPTIONS}
                value={preferencesForm.language}
                onChange={(value) =>
                  setPreferencesForm((prev) => ({ ...prev, language: value }))
                }
                className="mt-1"
                placeholder={t('settings.preferences.selectLanguage')}
                searchPlaceholder={t('settings.preferences.searchLanguages')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">{t('settings.preferences.timezone')}</label>
              <SearchableSelect
                options={timeZoneSelectOptions}
                value={preferencesForm.timezone}
                onChange={(value) =>
                  setPreferencesForm((prev) => ({ ...prev, timezone: value }))
                }
                className="mt-1"
                placeholder={t('settings.preferences.selectTimezone')}
                searchPlaceholder={t('settings.preferences.searchTimezones')}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={handlePreferencesSave} isLoading={prefsSaving}>
              {t('settings.preferences.save')}
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Link2 className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('settings.oauth.title', { defaultValue: 'Linked accounts' })}
            </h2>
          </div>

          {oauthAccountsError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{oauthAccountsError}</p>
            </div>
          )}

          {oauthAccountsLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t('settings.oauth.loading', { defaultValue: 'Loading…' })}</span>
            </div>
          ) : (
            (() => {
              const googleAccount = (oauthAccounts || []).find((a) => a.provider === 'google');
              return googleAccount ? (
                <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-slate-200 dark:border-zinc-700">
                  <div className="flex items-center gap-3 min-w-0">
                    {googleAccount.avatar_url && !isAvatarFailed(googleAccount.avatar_url) ? (
                      <img
                        src={googleAccount.avatar_url}
                        alt=""
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        decoding="async"
                        onError={() => markAvatarFailed(googleAccount.avatar_url)}
                        className="h-10 w-10 rounded-full object-cover border border-slate-200 dark:border-zinc-700"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-zinc-800 flex items-center justify-center text-slate-600 dark:text-zinc-300 text-sm font-semibold">
                        G
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">Google</p>
                      <p className="text-xs text-slate-500 dark:text-zinc-500 truncate">
                        {googleAccount.email || googleAccount.name || t('settings.oauth.linked', { defaultValue: 'Linked' })}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-zinc-800/40 border border-slate-200 dark:border-zinc-700">
                  <p className="text-sm text-slate-600 dark:text-zinc-400">
                    {t('settings.oauth.none', { defaultValue: 'No linked accounts yet.' })}
                  </p>
                  <div className="mt-4">
                    <GoogleLoginButton mode="link" redirectUrl="/settings" />
                  </div>
                </div>
              );
            })()
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="h-5 w-5 text-slate-500 dark:text-zinc-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('settings.apiKeys.title', { defaultValue: 'API Keys' })}
            </h2>
          </div>

          {apiKeysError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{apiKeysError}</p>
            </div>
          )}

          {subscriptionLoading ? (
            <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t('settings.apiKeys.loading', { defaultValue: 'Loading…' })}</span>
            </div>
          ) : !isPaidUser ? (
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-zinc-800/40 border border-slate-200 dark:border-zinc-700">
              <p className="text-sm text-slate-600 dark:text-zinc-400">
                {t('settings.apiKeys.paidOnly', { defaultValue: 'API Keys are available for paid plans only.' })}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {t('settings.apiKeys.nameLabel', { defaultValue: 'Name' })}
                  </label>
                  <input
                    type="text"
                    value={createApiKeyName}
                    onChange={(event) => setCreateApiKeyName(event.target.value)}
                    placeholder={t('settings.apiKeys.namePlaceholder', { defaultValue: 'e.g., n8n, backend, personal' })}
                    className="mt-1 w-full rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleCreateApiKey}
                    isLoading={createApiKeyLoading}
                    disabled={!createApiKeyName.trim()}
                  >
                    {t('settings.apiKeys.create', { defaultValue: 'Create API Key' })}
                  </Button>
                </div>
              </div>

              <div className="border border-slate-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 dark:bg-zinc-800/50 flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {t('settings.apiKeys.listTitle', { defaultValue: 'Your keys' })}
                  </p>
                  {apiKeysLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-500 dark:text-zinc-400" />}
                </div>
                <div className="divide-y divide-slate-200 dark:divide-zinc-700">
                  {!apiKeysLoading && (!apiKeys || apiKeys.length === 0) ? (
                    <div className="px-4 py-4 text-sm text-slate-500 dark:text-zinc-500">
                      {t('settings.apiKeys.empty', { defaultValue: 'No API keys yet.' })}
                    </div>
                  ) : (
                    (apiKeys || []).map((key) => (
                      <div key={key.id} className="px-4 py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {key.name || t('settings.apiKeys.unnamed', { defaultValue: 'Unnamed key' })}
                            </p>
                            {key.revoked_at && (
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-zinc-700 dark:text-zinc-200">
                                {t('settings.apiKeys.revoked', { defaultValue: 'Revoked' })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-zinc-500 truncate">
                            {t('settings.apiKeys.prefix', { defaultValue: 'Prefix' })}: {key.key_prefix}
                            {key.last_used_at ? ` · ${t('settings.apiKeys.lastUsed', { defaultValue: 'Last used' })}: ${new Date(key.last_used_at).toLocaleString()}` : ''}
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {key.revoked_at ? (
                            <Button variant="danger" onClick={() => openDeleteApiKeyModal(key)}>
                              {t('settings.apiKeys.delete', { defaultValue: 'Delete' })}
                            </Button>
                          ) : (
                            <Button variant="outline" onClick={() => openRevokeModal(key)}>
                              {t('settings.apiKeys.revoke', { defaultValue: 'Revoke' })}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border border-red-200 dark:border-red-900/50 bg-red-50/30 dark:bg-red-900/10">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 className="h-5 w-5 text-red-500 dark:text-red-400" />
            <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">{t('settings.danger.title')}</h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-zinc-400">
            {t('settings.danger.description')}
          </p>
          <div className="mt-4">
            <Button variant="danger" onClick={() => setDeleteModalOpen(true)}>
              {t('settings.danger.deleteAccount')}
            </Button>
          </div>
        </Card>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteModalOpen(false);
          setDeletePassword('');
          setDeleteError('');
        }}
        title={t('settings.danger.confirmTitle')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleteLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteAccount} isLoading={deleteLoading}>
              {t('settings.danger.deleteAccount')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-zinc-300">
            {t('settings.danger.confirmDescription')}
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">{t('common.password')}</label>
            <input
              type="password"
              value={deletePassword}
              onChange={(event) => setDeletePassword(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          {deleteError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{deleteError}</p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={newApiKeyModalOpen}
        onClose={() => {
          setNewApiKeyModalOpen(false);
          setNewApiKeyPlain('');
          setCopiedApiKey(false);
        }}
        title={t('settings.apiKeys.newKeyTitle', { defaultValue: 'New API Key' })}
        footer={
          <Button
            variant="outline"
            onClick={() => {
              setNewApiKeyModalOpen(false);
              setNewApiKeyPlain('');
              setCopiedApiKey(false);
            }}
          >
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-zinc-300">
            {t('settings.apiKeys.oneTimeNotice', { defaultValue: 'Copy this key now. You won’t be able to see it again.' })}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newApiKeyPlain}
              readOnly
              className="flex-1 rounded-lg border border-slate-300 dark:border-zinc-700 px-3 py-2 text-sm bg-white dark:bg-zinc-900 text-slate-900 dark:text-white font-mono"
            />
            <Button variant="outline" onClick={handleCopyApiKey}>
              {copiedApiKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={revokeModalOpen}
        onClose={() => {
          if (revokeLoading) return;
          setRevokeModalOpen(false);
          setRevokeTarget(null);
        }}
        title={t('settings.apiKeys.revokeTitle', { defaultValue: 'Revoke API Key' })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setRevokeModalOpen(false);
                setRevokeTarget(null);
              }}
              disabled={revokeLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmRevoke} isLoading={revokeLoading}>
              {t('settings.apiKeys.revoke', { defaultValue: 'Revoke' })}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-zinc-300">
          {t('settings.apiKeys.revokeConfirm', { defaultValue: 'This key will stop working immediately.' })}
        </p>
      </Modal>

      <Modal
        isOpen={deleteApiKeyModalOpen}
        onClose={() => {
          if (deleteApiKeyLoading) return;
          setDeleteApiKeyModalOpen(false);
          setDeleteApiKeyTarget(null);
        }}
        title={t('settings.apiKeys.deleteTitle', { defaultValue: 'Delete API Key' })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteApiKeyModalOpen(false);
                setDeleteApiKeyTarget(null);
              }}
              disabled={deleteApiKeyLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmDeleteApiKey} isLoading={deleteApiKeyLoading}>
              {t('settings.apiKeys.delete', { defaultValue: 'Delete' })}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-zinc-300">
          {t('settings.apiKeys.deleteConfirm', { defaultValue: 'This will permanently delete the key. This action cannot be undone.' })}
        </p>
      </Modal>
    </div>
  );
};

export default Settings;
